import { useCallback, useEffect } from 'react';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import goSound from '@asset/sound/go.mp3';
import tapSound from '@asset/sound/tap.mp3';
// import click3Sound from '@asset/sound/click-3.mp3';
import banSound from '@asset/sound/ban.mp3';
import correctSound from '@asset/sound/correct.mp3';
import incorrectSound from '@asset/sound/incorrect.mp3';
import typingSound from '@asset/sound/typing.mp3';
import typingSoftSound from '@asset/sound/typing-soft.mp3';
import typingHardSound from '@asset/sound/typing-hard.mp3';

type TSoundType =
  | 'go' // 똥 — 진행/진입 (학습하기, 다음문제, 이야기학습)
  | 'tap' // 뽁 — 가벼운 선택 (탭 전환, 보기 클릭, 저장하기)
  | 'ban' // 차단/거절 — 로그인 필요 모달 등 액션 막힘
  // | 'click-deprecated' // 챡 — 사용 보류
  | 'correct' // 정답 O
  | 'incorrect' // 오답 X
  | 'typing' // 키보드 타이핑 (loop 전용, 단일 인스턴스)
  | 'typing-soft' // 부드러운 타자기 (글자별)
  | 'typing-hard'; // 단단한 타자기 (글자별)

const SOUND_SOURCES: Record<TSoundType, string> = {
  go: goSound,
  tap: tapSound,
  ban: banSound,
  // 'click-deprecated': click3Sound,
  correct: correctSound,
  incorrect: incorrectSound,
  typing: typingSound,
  'typing-soft': typingSoftSound,
  'typing-hard': typingHardSound,
};

interface ISoundOptions {
  loop?: boolean;
  volume?: number;
}

// 일시 비활성화 플래그: 데스크탑/모바일 모두 무음
// (iOS 무음 스위치 무시 이슈 — 앱 내 음소거 토글 추가 후 false로 복귀 예정)
const IS_SOUND_DISABLED = false;

// 단발 사운드 풀 사이즈 (round-robin) — 빠른 연타 시 인스턴스 충돌 방지
const POOL_SIZE = 5;

// loop 전용 type — 풀 사용 안 함, 단일 인스턴스만 유지
const LOOP_ONLY_TYPES: TSoundType[] = ['typing', 'typing-soft', 'typing-hard'];

// AudioContext (모듈 전역, 1회 생성)
// - HTMLAudioElement → Web Audio API로 교체: WKWebView에서 매 재생 시 디코딩 비용 0
// - AudioBuffer를 미리 디코딩해 두고, 재생 시점엔 BufferSource로 즉시 출력
let audioContext: AudioContext | null = null;

// 디코딩된 AudioBuffer 풀 (모듈 전역)
// - 풀 구조 유지: 단발 POOL_SIZE개 / loop 1개. round-robin은 poolIndexMap이 담당
// - 단, AudioBuffer는 read-only & 다중 재생 안전 → 실제론 같은 buffer를 N번 참조해도 무방하지만
//   기존 구조(인스턴스 풀)와의 호환을 위해 배열 형태 유지
let audioPoolMap: Record<TSoundType, AudioBuffer[]> | null = null;
const poolIndexMap: Record<string, number> = {};

// loop 전용 source 보관 (정지용)
const loopSourceMap: Partial<Record<TSoundType, AudioBufferSourceNode>> = {};

const getAudioContext = (): AudioContext | null => {
  if (audioContext) return audioContext;
  const Ctor = window.AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) return null;
  audioContext = new Ctor();
  return audioContext;
};

/**
 * 앱 시작 시 모든 효과음을 백그라운드 다운로드하는 훅
 * - 앱 루트(IyagigoApp)에서 한 번만 호출
 * - 단발 사운드는 풀(POOL_SIZE)로, loop 사운드는 단일 인스턴스로 캐싱
 *
 * 현재 사운드 일시 비활성화(IS_SOUND_DISABLED=true) 상태라 본 훅은 no-op으로 동작.
 * 활성화 시 IS_SOUND_DISABLED를 false로 바꾸면 다운로드/풀 생성 재개됨.
 */
export const useDownloadSound = () => {
  useEffect(() => {
    if (IS_SOUND_DISABLED) return;
    const ctx = getAudioContext();
    if (!ctx) return;

    const map = {} as Record<TSoundType, AudioBuffer[]>;
    (Object.keys(SOUND_SOURCES) as TSoundType[]).forEach(async (type) => {
      const isLoopOnly = LOOP_ONLY_TYPES.includes(type);
      const count = isLoopOnly ? 1 : POOL_SIZE;
      poolIndexMap[type] = 0;
      try {
        const res = await fetch(SOUND_SOURCES[type]);
        const arrayBuffer = await res.arrayBuffer();
        const buffer = await ctx.decodeAudioData(arrayBuffer);
        // AudioBuffer는 read-only & 동시 재생 안전 → 같은 buffer를 count개 참조
        map[type] = Array.from({ length: count }, () => buffer);
      } catch {
        map[type] = [];
      }
    });
    audioPoolMap = map;

    // iOS WKWebView 오디오 세션 unlock:
    // - Web Audio API만 단독 사용 시 iOS가 페이지를 'ambient/silent' 카테고리로 판단해 무음 처리하는 케이스 있음 (실제 관찰됨)
    // - HTMLAudioElement가 한 번 재생되면 'playback' 카테고리로 전환되어 Web Audio도 정상 재생됨
    // → 첫 사용자 제스처 시 무음 audio 1회 재생 + AudioContext resume. 백그라운드 복귀 후에도 동일.
    // 2026-10-13: 무음 HTML 오디오가 iOS 네이티브 녹음 세션을 중단시키는
    // 현상을 확인해 비활성화한다. 기존 효과음 우회 코드는 참고용으로 보존한다.
    // const silentAudio = new Audio(
    //   'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=',
    // );
    // silentAudio.loop = false;
    const unlockOnGesture = () => {
      if (audioContext && audioContext.state !== 'running') {
        audioContext.resume().catch(() => {});
      }
      // silentAudio.play().catch(() => {});
    };
    document.addEventListener('touchstart', unlockOnGesture, { passive: true });
    document.addEventListener('click', unlockOnGesture);
    return () => {
      document.removeEventListener('touchstart', unlockOnGesture);
      document.removeEventListener('click', unlockOnGesture);
    };
  }, []);
};

/**
 * 효과음 재생 훅 (모바일 환경에서만 동작)
 * - 단발 재생: round-robin 풀로 빠른 연타에도 인스턴스 충돌 없이 재생
 * - 루프 재생: 단일 인스턴스 (typing 등)
 * - 다운로드는 useDownloadSound가 담당, 이 훅은 재생만 함
 *
 * 사용 예:
 *   const playSound = usePlaySound();
 *   playSound('tap');                                  // 단발 (풀에서 다음 인스턴스)
 *   const stop = playSound('typing', { loop: true });  // 루프 (정지 함수 반환)
 *   stop();                                            // 정지
 *
 * @param defaultVolume 기본 볼륨 (0~1, 기본 0.5)
 * @returns 호출 시 type/options 지정해 효과음을 재생하는 함수. loop=true면 정지 함수 반환
 */
export const usePlaySound = (defaultVolume = 0.5) => {
  return useCallback(
    (type: TSoundType = 'go', options: ISoundOptions = {}): (() => void) => {
      if (IS_SOUND_DISABLED || !IS_IYAGIGO_MOBILE_VIEW) return () => {};

      const ctx = getAudioContext();
      const pool = audioPoolMap?.[type];
      if (!ctx || !pool || pool.length === 0) return () => {};

      // 단발이면 round-robin, loop이면 항상 0번 인스턴스
      const isLoop = options.loop ?? false;
      const idx = isLoop ? 0 : (poolIndexMap[type] ?? 0);
      const buffer = pool[idx];

      if (!isLoop) {
        poolIndexMap[type] = (idx + 1) % pool.length;
      }

      // loop 동일 type 중복 재생 방지: 기존 source 정지
      if (isLoop) {
        const prev = loopSourceMap[type];
        if (prev) {
          try {
            prev.stop();
          } catch {}
        }
      }

      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = isLoop;

      const gain = ctx.createGain();
      // iOS에서 gain 즉시 변경 시 클릭/씹힘 발생 가능 → 명시적 schedule
      gain.gain.setValueAtTime(options.volume ?? defaultVolume, ctx.currentTime);

      source.connect(gain).connect(ctx.destination);

      // iOS 간헐 무음 이슈 대응 (iOS 16+ WKWebView 기준):
      // - 백그라운드 진입 없어도 시리/전화/알림/외부 오디오로 state가 'interrupted'가 될 수 있음
      // - 일정 시간 idle 시 iOS가 AudioContext를 'suspended'로 자동 전환하기도 함
      // - resume()은 비동기 — 완료 전 start() 호출 시 첫 재생이 무음 처리됨
      // 따라서 'running'이 아니면 매번 resume 후 start. 반환 인터페이스는 동기 stop 함수 유지.
      const startSource = () => {
        try {
          source.start(0);
        } catch (error) {
          // Error 객체 자체는 WebView 브리지에서 {}로 직렬화될 수 있다.
          console.warn('=== sound playback failed ===', {
            type,
            contextState: ctx.state,
            errorName: error instanceof Error ? error.name : undefined,
            errorMessage: error instanceof Error ? error.message : String(error),
          });
        }
      };
      if (ctx.state !== 'running') {
        ctx.resume().then(startSource, (error) => {
          console.warn('=== sound resume failed ===', {
            type,
            contextState: ctx.state,
            errorName: error?.name,
            errorMessage: error?.message ?? String(error),
          });
          // 기존 동작 유지: 재개 실패 후에도 재생을 시도한다.
          startSource();
        });
      } else {
        startSource();
      }

      if (isLoop) {
        loopSourceMap[type] = source;
      }

      return () => {
        try {
          source.stop();
        } catch {}
        if (isLoop && loopSourceMap[type] === source) {
          loopSourceMap[type] = undefined;
        }
      };
    },
    [defaultVolume],
  );
};
